import { customApi } from 'shared/api/customApi'

/**
 * Decodes a JWT payload without verifying the signature.
 * Only used client-side to extract claims from a trusted token.
 */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
    try {
        const base64Url = token.split('.')[1]
        if (!base64Url) return null
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
        const jsonPayload = decodeURIComponent(
            atob(base64)
                .split('')
                .map(
                    (c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
                )
                .join('')
        )
        return JSON.parse(jsonPayload)
    } catch {
        return null
    }
}

function getRefreshToken(): string {
    const raw =
        sessionStorage.getItem('refreshToken') ||
        localStorage.getItem('refreshToken')
    if (!raw) return ''
    try {
        const parsed = JSON.parse(raw)
        return typeof parsed === 'string' ? parsed : ''
    } catch {
        return raw
    }
}

interface JwtUserRole {
    id: number
    key: string
    organizationId?: string
    roleId: number
}

/**
 * Extract a comparable fingerprint from JWT userRoles.
 * Sorted string of "id:key" pairs so order doesn't matter.
 */
function roleFingerprint(roles: JwtUserRole[]): string {
    return roles
        .map((r) => `${r.id}:${r.key}:${r.organizationId ?? ''}`)
        .sort()
        .join('|')
}

/**
 * Get the current user's role fingerprint from stored access token.
 */
function getCurrentRoleFingerprint(): string {
    const tokenRaw =
        sessionStorage.getItem('accessToken') ||
        localStorage.getItem('accessToken')
    if (!tokenRaw) return ''
    try {
        const token =
            typeof tokenRaw === 'string' && tokenRaw.startsWith('"')
                ? JSON.parse(tokenRaw)
                : tokenRaw
        const payload = decodeJwtPayload(token)
        if (!payload?.userRoles) return ''
        return roleFingerprint(payload.userRoles as JwtUserRole[])
    } catch {
        return ''
    }
}

/**
 * Refreshes the access token and checks if the user's roles changed.
 * If roles changed, updates storage and notifies the AbilityProvider.
 * If roles are the same, does nothing (avoids corrupting stored data).
 *
 * @param dispatch - Redux store dispatch
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function refreshUserRoles(dispatch: any): Promise<boolean> {
    const refreshToken = getRefreshToken()
    if (!refreshToken) return false

    // Capture the current role fingerprint BEFORE refresh
    const oldFingerprint = getCurrentRoleFingerprint()

    try {
        const result = (await dispatch(
            customApi.endpoints.refreshToken.initiate(refreshToken)
        )) as { data?: { accessToken?: string } }

        const newAccessToken = result.data?.accessToken
        if (!newAccessToken) return false

        // Decode the new JWT
        const payload = decodeJwtPayload(newAccessToken)
        if (!payload?.userRoles) return false

        const newFingerprint = roleFingerprint(
            payload.userRoles as JwtUserRole[]
        )

        // If roles haven't changed, do nothing
        if (oldFingerprint === newFingerprint) return false

        // Roles changed — update stored user data with new roles from JWT.
        // Build role assignments in the format the app expects.
        const jwtRoles = payload.userRoles as JwtUserRole[]
        const newUserRoles = jwtRoles.map((r) => ({
            id: r.id,
            roleId: r.roleId,
            organizationId: r.organizationId ?? null,
            role: { id: r.roleId, key: r.key },
            units: [],
            sports: [],
        }))

        for (const storage of [sessionStorage, localStorage]) {
            const userRaw = storage.getItem('user')
            if (userRaw) {
                try {
                    const userData = JSON.parse(userRaw)
                    userData.userRoles = newUserRoles
                    storage.setItem('user', JSON.stringify(userData))
                } catch {
                    // Skip if user data can't be parsed
                }
            }
        }

        // Notify AbilityProvider to re-read roles
        window.dispatchEvent(new CustomEvent('user-data-updated'))
        return true
    } catch {
        return false
    }
}
